import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { utils as $utils } from '@/utils'

dayjs.extend(utc)

/**
 * Embargo helpers (ND-2359). An embargo is a UTC instant on the asset and
 * "embargoed" means that instant is still ahead of us. The API sends
 * is_embargoed already derived, but a row patched in place only carries the
 * new embargo_until, so derive from the timestamp whenever one is present.
 */
export function isEmbargoed(asset) {
  if (!asset) return false
  if (asset.embargo_until) return dayjs.utc(asset.embargo_until).isAfter(dayjs())
  return asset.is_embargoed === true
}

/**
 * Restricted for sharing: a restricted tag (the API's is_restricted) or an
 * active embargo. The API already folds the embargo into is_restricted; this
 * covers rows patched in place, which only carry the new embargo_until.
 */
export function isRestricted(asset) {
  return asset?.is_restricted === true || isEmbargoed(asset)
}

/** Badge text in the workspace timezone. */
export function embargoLabel(asset) {
  return asset?.embargo_until ? `Embargoed until ${$utils.formatDateTime(asset.embargo_until)}` : ''
}

/** "Embargo lifts in 3 hours" (or "Embargo lifted 2 days ago" once it has passed). */
export function embargoLiftsIn(embargoUntil) {
  if (!embargoUntil) return ''
  const until = dayjs.utc(embargoUntil)
  return until.isAfter(dayjs()) ? `Embargo lifts ${until.fromNow()}` : `Embargo lifted ${until.fromNow()}`
}

/**
 * Rich tooltip for the embargo badges (render with v-tooltip html: true).
 * Relative time first, the exact instant in the workspace timezone, then what
 * an embargo means: a hold signal that downstream tools honor, not a lock
 * inside NewsDesk.
 */
export function embargoTooltip(asset) {
  if (!asset?.embargo_until) return ''
  return [
    `<div class="font-semibold">${embargoLiftsIn(asset.embargo_until)}</div>`,
    '<div class="mt-1 text-xs opacity-80" style="max-width: 260px">',
    'Until then this clip is treated like a restricted tag: it cannot be shared to other stations or copied across workspaces, and automated rundown tools skip it. It can still be viewed, edited, and downloaded here. Set, change, or remove it from the media menu.',
    '</div>'
  ].join('')
}

/**
 * The latest still-active embargo among the assets, or null. A script that
 * references several embargoed videos is embargoed until the last one lifts.
 */
export function latestEmbargo(assets) {
  return (assets || []).reduce((latest, asset) => {
    if (!isEmbargoed(asset)) return latest
    return !latest || dayjs.utc(asset.embargo_until).isAfter(dayjs.utc(latest)) ? asset.embargo_until : latest
  }, null)
}
